import React, { useState, useEffect } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { db, storage } from "../firebase";
import { ref as dbRef, onValue, update, remove } from "firebase/database";
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { Edit, ArrowLeft, Upload, AlertCircle, ChevronRight, FileText, Eye, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "react-toastify";

const SocietyDocumentList = () => {
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [currentFile, setCurrentFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [userInfo, setUserInfo] = useState(null);

  const documents = [
    { id: 1, name: "संस्था नोंदणीचे प्रमाणपत्र (महाराष्ट्र राज्य सहकारी संस्था अधिनियम १९६०)", englishName: "Society Registration Certificate" },
    { id: 2, name: "मागील वार्षिक लेखा परीक्षणाचा अहवाल व तपासणीचा वर्ग", englishName: "Audit Report & Rating" },
    { id: 3, name: "मजूर सभासदाचे नाव पत्ता व छायाचित्रा सहित ओळखपत्राची सत्यप्रत", englishName: "Member ID List" },
    { id: 4, name: "सभासदांसाठी अपघाती विमा योजना सुरु केले बाबत प्रमाणपत्राची सत्यप्रत", englishName: "Accident Insurance Proof" },
    { id: 5, name: "जिल्हा उपनिबंधक यांचे वर्गीकरण प्रमाणपत्राची प्रत", englishName: "DDR Classification Certificate" },
    { id: 6, name: "चेरमन /सभासद नावाबाबतची ठराविक नक्कल व सह्यांचे नमुन्याची नक्कल", englishName: "Chairman/Member Signatures & Resolutions" },
    { id: 7, name: "मागील तीन वर्षात केलेल्या कामाचा व हाती/प्रगतीत असलेल्या कामांचा दाखला", englishName: "Work Experience Certificate" },
    { id: 8, name: "मागील आर्थिक वर्षाचे तेरीजपत्रक, नफा तोटा पत्रक आणि ताळेबंद", englishName: "Balance Sheet & P&L Statement" },
    { id: 9, name: "क्षमता, आर्थिक पात्रता या बाबतची प्रमाणपत्रे (जिल्हा निबंधक/उपनिबंधक)", englishName: "Financial Capacity Certificate" },
    { id: 10, name: "नोंदणी फी पावती", englishName: "Registration Fee Receipt" },
    { id: 11, name: "पॅनकार्ड प्रमाणपत्र", englishName: "Society PAN Card" },
    { id: 12, name: "जिल्हा उपनिबंधक यांचे वैधता पत्र", englishName: "Validity Letter from DDR" },
    { id: 13, name: "सभासद यादी व संचालक मंडळ", englishName: "Member List & Board of Directors" },
    { id: 14, name: "सभासद मस्टर", englishName: "Member Muster" },
    { id: 15, name: "बँक पासबुक", englishName: "Bank Passbook" }
  ];

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('currentUser'));
    if (user) {
      setUserInfo(user);
      // Standardized path: ekamwatapzp/users/{key}/documents
      const docsRef = dbRef(db, `ekamwatapzp/users/${user._key}/documents`);
      
      const unsubscribe = onValue(docsRef, (snapshot) => {
        if (snapshot.exists()) {
          setUploadedFiles(snapshot.val());
        } else {
          setUploadedFiles({});
        }
        setLoading(false);
      });
      return () => unsubscribe();
    } else {
      setLoading(false);
    }
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("फाईल 2MB पेक्षा लहान असावी.");
        return;
      }
      setCurrentFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!currentFile || !userInfo) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const fileExt = currentFile.name.split('.').pop();
      const fileName = `${selectedDoc.id}_${Date.now()}.${fileExt}`;
      const fileRef = storageRef(storage, `ekamwatapzp/documents/${userInfo._key}/${fileName}`);
      
      const uploadTask = uploadBytesResumable(fileRef, currentFile);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(Math.round(progress));
        },
        (error) => {
          toast.error("अपलोड अयशस्वी झाले.");
          setUploading(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          // Standardized path: ekamwatapzp/users/{key}/documents
          const userDocsRef = dbRef(db, `ekamwatapzp/users/${userInfo._key}/documents`);
          const newDocData = {
            [selectedDoc.id]: {
              url: downloadURL,
              storagePath: `ekamwatapzp/documents/${userInfo._key}/${fileName}`,
              name: currentFile.name,
              type: currentFile.type,
              uploadedAt: new Date().toISOString()
            }
          };

          await update(userDocsRef, newDocData);
          toast.success(`${selectedDoc.name} यशस्वीरीत्या अपडेट झाले!`);
          setSelectedDoc(null);
          setCurrentFile(null);
          setPreviewUrl(null);
          setUploading(false);
        }
      );
    } catch (error) {
      toast.error("काहीतरी चूक झाली.");
      setUploading(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-blue-600 mb-4" size={48} />
        <p className="text-slate-600 font-bold tracking-widest uppercase text-xs">डेटा लोड होत आहे...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-slate-50">
      <Navbar />
      
      <main className="flex-grow py-8 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
             <div>
                <h1 className="text-3xl font-black text-blue-900 leading-tight">संस्था कागदपत्रे अपलोड</h1>
                <p className="text-slate-500 font-medium mt-1">मजूर सहकारी संस्थेसाठी आवश्यक सर्व कागदपत्रे येथे अपलोड करा.</p>
             </div>
             <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-slate-200 text-sm font-bold text-slate-600 flex items-center gap-2">
                सांगली जिल्हा परिषद <ChevronRight size={16} className="text-slate-400" /> <span className="text-blue-600 font-black">संस्था दस्तावेज</span>
             </div>
          </div>

          {!selectedDoc ? (
            <div className="bg-white rounded-3xl shadow-xl border border-blue-50 overflow-hidden animate-in fade-in slide-in-from-bottom-5 duration-500">
               <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100">
                        <th className="px-6 py-6 text-center font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] border-r border-slate-100 w-20">#</th>
                        <th className="px-6 py-6 text-left font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] border-r border-slate-100">कागदपत्रांची यादी</th>
                        <th className="px-6 py-6 text-center font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] border-r border-slate-100">स्थिती</th>
                        <th className="px-6 py-6 text-center font-black text-slate-400 text-[10px] uppercase tracking-[0.2em]">कृती</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {documents.map((doc) => {
                        const uploaded = uploadedFiles[doc.id];
                        return (
                          <tr key={doc.id} className="hover:bg-blue-50/30 transition-all group">
                            <td className="px-6 py-5 border-r border-slate-50 text-center font-black text-slate-300 group-hover:text-blue-200">{doc.id}</td>
                            <td className="px-6 py-5 border-r border-slate-50">
                               <div className="flex flex-col">
                                  <span className="font-bold text-slate-700 group-hover:text-blue-900 transition-colors">{doc.name}</span>
                                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">{doc.englishName}</span>
                               </div>
                            </td>
                            <td className="px-6 py-5 border-r border-slate-50 text-center">
                               {uploaded ? (
                                 <div className="inline-flex flex-col items-center gap-1">
                                    <span className="bg-emerald-50 text-emerald-600 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider flex items-center gap-1 border border-emerald-100">
                                       <CheckCircle2 size={12} /> अपलोड
                                    </span>
                                    <a href={uploaded.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-[9px] font-bold underline flex items-center gap-0.5 mt-1 hover:text-blue-600">
                                       <Eye size={10} /> फाईल पहा
                                    </a>
                                 </div>
                               ) : (
                                 <span className="bg-rose-50 text-rose-500 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider flex items-center gap-1 mx-auto w-fit border border-rose-100">
                                    <XCircle size={12} /> नाही
                                 </span>
                               )}
                            </td>
                            <td className="px-6 py-5">
                               <div className="flex items-center justify-center gap-2">
                                  <button 
                                    onClick={() => setSelectedDoc(doc)}
                                    className={`px-4 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all shadow-sm ${
                                      uploaded ? 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-blue-200 hover:text-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-100'
                                    }`}
                                  >
                                    <Edit size={14} /> {uploaded ? 'बदला' : 'अपलोड'}
                                  </button>
                               </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
               </div>
            </div>
          ) : (
            <div className="bg-white rounded-[2rem] shadow-2xl border border-slate-100 p-10 max-w-5xl mx-auto animate-in zoom-in-95 duration-500">
               <button onClick={() => {setSelectedDoc(null); setPreviewUrl(null); setCurrentFile(null);}} className="flex items-center gap-2 text-slate-400 hover:text-slate-600 font-black text-[10px] uppercase tracking-[0.2em] mb-10 transition-colors">
                  <ArrowLeft size={18} /> मुख्य यादीवर परत जा
               </button>

               <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
                  <div className="lg:col-span-3 space-y-8">
                     <header>
                        <div className="bg-blue-600 h-1 w-12 rounded-full mb-4"></div>
                        <h2 className="text-3xl font-black text-slate-900 tracking-tight leading-tight">{selectedDoc.englishName}</h2>
                        <p className="text-slate-500 font-medium text-lg mt-2">{selectedDoc.name}</p>
                     </header>

                     <div className="bg-slate-50 rounded-3xl p-8 border border-slate-200 space-y-8">
                        <div className="space-y-4">
                           <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Choose Document File</label>
                           <input 
                             type="file" 
                             accept="application/pdf,image/*" 
                             onChange={handleFileChange}
                             disabled={uploading}
                             className="block w-full text-xs text-slate-500 file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border-0 file:text-[11px] file:font-black file:uppercase file:bg-slate-900 file:text-white hover:file:bg-slate-800 transition-all cursor-pointer border border-slate-200 rounded-2xl bg-white p-2"
                           />
                        </div>

                        {uploading && (
                          <div className="space-y-3">
                             <div className="flex justify-between text-[11px] font-black text-blue-600 uppercase tracking-widest">
                                <span>अपलोडिंग सुरू आहे...</span>
                                <span>{uploadProgress}%</span>
                             </div>
                             <div className="h-3 bg-white border border-slate-100 rounded-full overflow-hidden shadow-inner">
                                <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300" style={{width: `${uploadProgress}%`}}></div>
                             </div>
                          </div>
                        )}

                        <div className="flex items-start gap-3 bg-blue-50/50 p-5 rounded-2xl border border-blue-100">
                           <AlertCircle className="text-blue-500 shrink-0" size={20} />
                           <p className="text-xs text-blue-800/70 font-bold leading-relaxed">
                              कृपया खात्री करा की तुमची फाईल <strong>PDF</strong> किंवा <strong>Image</strong> फॉरमॅटमध्ये आहे आणि <strong>2MB</strong> पेक्षा कमी आहे. कागदपत्र स्पष्ट वाचता येण्यासारखे असावे.
                           </p>
                        </div>

                        <div className="flex gap-4">
                            <button 
                              onClick={handleUpdate}
                              disabled={!currentFile || uploading}
                              className={`flex-grow py-5 rounded-2xl font-black uppercase tracking-[0.2em] text-xs shadow-2xl transition-all active:scale-95 ${
                                !currentFile || uploading ? 'bg-slate-100 text-slate-300 cursor-not-allowed shadow-none' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-200'
                              }`}
                            >
                               {uploading ? <Loader2 className="animate-spin mx-auto" size={20} /> : 'कागदपत्र अपडेट करा (Update now)'}
                            </button>
                        </div>
                     </div>
                  </div>

                  <div className="lg:col-span-2">
                     <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4">दस्तावेज पूर्वावलोकन (Preview)</label>
                     <div className="bg-slate-900 rounded-[2.5rem] aspect-[4/5] flex flex-col items-center justify-center p-10 text-center border-[12px] border-slate-50 shadow-2xl relative overflow-hidden group">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent)] group-hover:scale-150 transition-transform duration-1000"></div>
                        
                        {previewUrl ? (
                           <div className="w-full h-full flex items-center justify-center animate-in zoom-in-90 duration-500 relative z-10">
                              {currentFile.type === 'application/pdf' ? (
                                <div className="space-y-4">
                                   <div className="bg-rose-500 p-6 rounded-[2rem] shadow-2xl shadow-rose-900/50 border-4 border-white/20">
                                      <FileText size={80} className="text-white" />
                                   </div>
                                   <p className="text-white font-black text-xs uppercase tracking-widest">{currentFile.name}</p>
                                </div>
                              ) : (
                                <img src={previewUrl} alt="Preview" className="max-h-full rounded-2xl shadow-2xl border-2 border-white/10" />
                              )}
                           </div>
                        ) : uploadedFiles[selectedDoc.id] ? (
                           <div className="w-full h-full flex flex-col items-center justify-center animate-in fade-in duration-500 relative z-10">
                              {uploadedFiles[selectedDoc.id].type === 'application/pdf' ? (
                                <div className="space-y-6">
                                   <div className="bg-emerald-500 p-7 rounded-[2rem] shadow-2xl shadow-emerald-900/50 border-4 border-white/20">
                                      <FileText size={80} className="text-white" />
                                   </div>
                                   <div className="space-y-2">
                                      <p className="text-emerald-400 font-black text-[10px] uppercase tracking-[0.3em]">Uploaded</p>
                                      <a href={uploadedFiles[selectedDoc.id].url} target="_blank" rel="noopener noreferrer" className="inline-block bg-white/10 hover:bg-white/20 text-white px-6 py-2 rounded-full text-[10px] font-bold transition-all border border-white/10">ओपन करा</a>
                                   </div>
                                </div>
                              ) : (
                                <div className="relative h-full flex flex-col items-center justify-center">
                                   <img src={uploadedFiles[selectedDoc.id].url} alt="Uploaded" className="max-h-full rounded-2xl shadow-2xl border-2 border-white/10" />
                                   <div className="absolute bottom-4 bg-emerald-500 text-white px-4 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-xl">Latest Version</div>
                                </div>
                              )}
                           </div>
                        ) : (
                           <div className="space-y-6 relative z-10 opacity-40">
                              <div className="bg-slate-800 p-8 rounded-full border border-slate-700">
                                 <Upload size={48} className="text-slate-600" />
                              </div>
                              <p className="text-slate-500 font-black text-xs uppercase tracking-widest italic">No document found..</p>
                           </div>
                        )}
                     </div>
                  </div>
               </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SocietyDocumentList;
